import DeliveryStatus from '~/client/src/shared/constants/DeliveryStatus'
import { getFormStatusDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import BaseNotification from '~/client/src/shared/models/Notification'
import User from '~/client/src/shared/models/User'
import Workflow from '~/client/src/shared/models/Workflow'

import { getTransitionPath } from '../../shared/constants/commonRoutes'
import Localization from '../../shared/localization/LocalizationManager'
import ProjectDateStore from '../../shared/stores/ui/ProjectDate.store'
import { isNotificationSupportedInEnv } from '../../shared/utils/util'
import desktopRoutes from '../constants/desktopRoutes'
import DesktopRootStore from './DesktopRoot.store'

// localization: translated

interface IDesktopNotification {
  title: string
  options: INotificationOptions
}

interface INotificationAction {
  action: string
  title: string
  icon: string
}

interface INotificationOptions {
  body: string
  icon: string
  tag: string
  actions: INotificationAction[]
}

const NOTIFICATION_ICON = '/static/icons/notification-logo.png'

export function generateAndSendDesktopNotifications(
  unreadNotifications: BaseNotification[],
  {
    deliveriesStore,
    gatesStore,
    zonesStore,
    companiesStore,
    projectDateStore,
    workflowTypesStore,
    workflowCategoriesStore,
    workflowsStore,
    messagesStore,
    projectMembersStore,
    userProjectsStore,
    state,
  }: DesktopRootStore,
) {
  const deliveryNotifications: IDesktopNotification[] = []
  const workflowNotifications: IDesktopNotification[] = []

  const seen = new Set<string>()
  const deduped = unreadNotifications.filter(n => {
    if (!n.entityId || seen.has(n.entityId)) return false
    seen.add(n.entityId)
    return true
  })

  deduped.forEach(({ entityId, messageId }) => {
    const delivery = deliveriesStore.byId.get(entityId)
    if (delivery) {
      const { status, gate, zone, startDate, endDate } = delivery
      const gateName = gatesStore.byId.get(gate)?.name
      const zoneName = zonesStore.byId.get(zone)?.name

      const dateInterval = getDateInterval(startDate, endDate, projectDateStore)
      deliveryNotifications.push({
        title: delivery.getDeliveryCaption(companiesStore),
        options: {
          body: `${dateInterval}, ${gateName}, ${zoneName}`,
          icon: NOTIFICATION_ICON,
          tag: `delivery-${entityId}`,
          actions: [
            {
              action: getTransitionPath(
                `${desktopRoutes.DELIVERIES}?${entityId}`,
                state.activeOrInitProjectCode,
              ),
              title: Localization.translator.openDelivery,
              icon: getDeliveryIcon(status),
            },
          ],
        },
      })
      return
    }

    const workflow = workflowsStore.getFormById(entityId)
    if (workflow) {
      const dateInterval = workflow.getDateAndTimeToDisplay(projectDateStore)
      const message = messagesStore.byId.get(messageId)
      const user = projectMembersStore.getById(message?.userId)
      const userName = User.getFullNameToDisplay(user, userProjectsStore, true)

      const body = `${workflow.getCategoryAndTypeCaption(
        workflowTypesStore,
        workflowCategoriesStore,
      )}\n${workflow.codeAndTitleCaption}\n${dateInterval}\n`

      workflowNotifications.push({
        title: `[${state.activeProject.code}] ${getFormStatusDisplayName(
          workflow.status,
          workflow.workflowStepLevel,
        )}`,
        options: {
          body: message
            ? `${body}\n@${userName} "${message.notificationText}"`
            : body,
          icon: NOTIFICATION_ICON,
          tag: `workflow-${entityId}`,
          actions: [
            {
              action: Workflow.getDirectLinkToInstance(
                state.activeOrInitProjectCode,
                workflow.id,
              ),
              title: Localization.translator.openWorkflow,
              icon: NOTIFICATION_ICON,
            },
          ],
        },
      })
      return
    }
  })

  sendDesktopNotifications(deliveryNotifications)
  sendDesktopNotifications(workflowNotifications)
}

function getDateInterval(
  startDate: Date | number,
  endDate: Date | number,
  projectDateStore: ProjectDateStore,
) {
  const { getPronounDateString, getTimeToDisplay } = projectDateStore
  const date = getPronounDateString(new Date(startDate))
  const startTime = getTimeToDisplay(startDate)
  const finishTime = getTimeToDisplay(endDate)
  return `${date}, ${startTime}-${finishTime}`
}

function sendDesktopNotifications(notifications: IDesktopNotification[]) {
  if (isNotificationSupportedInEnv()) {
    Notification.requestPermission(result => {
      if (result === 'granted') {
        navigator.serviceWorker.ready.then(registration => {
          notifications.forEach((notification, index) => {
            const title =
              notifications.length > 1
                ? notification.title +
                  `(${index + 1} of ${notifications.length})`
                : notification.title
            registration.showNotification(title, notification.options)
          })
        })
      }
    })
  }
}

function getDeliveryIcon(status: DeliveryStatus) {
  switch (status) {
    case DeliveryStatus.Requested:
    case DeliveryStatus.Changed:
      return '/static/icons/Requested.png'
    case DeliveryStatus.FailedInspection:
    case DeliveryStatus.Denied:
    case DeliveryStatus.Paused:
      return '/static/icons/Inspection-Rejected.png'
    case DeliveryStatus.OnSite:
    case DeliveryStatus.Delivering:
    case DeliveryStatus.OnHold:
      return '/static/icons/Onsite.png'
    case DeliveryStatus.PassedInspection:
      return '/static/icons/Inspection-Accepted.png'
    case DeliveryStatus.Scheduled:
    case DeliveryStatus.Canceled:
      return '/static/icons/DeliveryPlanned.png'
    case DeliveryStatus.Done:
      return '/static/icons/Done.png'
  }
}
